import { Link } from 'react-router-dom'
import { ArrowLeft, BotMessageSquare, Music, Clock, Settings, Zap } from 'lucide-react'
import { TextToMusicGallery } from '@/components/gallery/TextToMusicGallery'
import { MusicGalleryItem } from '@/models/gallery'
import * as stylex from '@stylexjs/stylex'
import { musicStyles as styles } from '@/styles/MusicPage.stylex'

// Model configuration - single source of truth
const MODEL_CONFIG = {
    name: 'Google Lyria',
    shortName: 'Google Lyria',
    modelKey: 'google-lyria',
    creditsPerRequest: 6,
    providerUrl: 'https://deepmind.google/lyria',
    providerName: 'Google DeepMind Lyria',
    pageUrl: 'https://docs.aibrush.co/music-models/google-lyria',
} as const;

const textToMusicGalleryItems: MusicGalleryItem[] = [
    {
        id: "lyria-music-1",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-1.wav",
        title: "Neon Dreams",
        mode: "music",
        vocalGender: "none",
        style: `A futuristic cyberpunk composition with pulsing electronic beats, atmospheric synth pads, and metallic percussion. The track features a driving 128 BPM rhythm with deep sub-bass, crisp hi-hats, and ethereal lead melodies that evoke the neon-lit streets of a digital metropolis.`,
        prompt: `A futuristic cyberpunk composition with pulsing electronic beats, atmospheric synth pads, and metallic percussion. The track features a driving 128 BPM rhythm with deep sub-bass, crisp hi-hats, and ethereal lead melodies that evoke the neon-lit streets of a digital metropolis.`,
    },
    {
        id: "lyria-music-2",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-2.wav",
        title: "Ocean Serenity",
        mode: "music",
        vocalGender: "none",
        style: `A peaceful ambient composition inspired by ocean waves, featuring gentle piano melodies, soft string arrangements, and subtle nature sounds. The piece should have a slow, meditative tempo around 60 BPM with flowing harmonies that create a sense of calm and tranquility.`,
        prompt: `A peaceful ambient composition inspired by ocean waves, featuring gentle piano melodies, soft string arrangements, and subtle nature sounds. The piece should have a slow, meditative tempo around 60 BPM with flowing harmonies that create a sense of calm and tranquility.`,
    },
    {
        id: "lyria-music-3",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-3.wav",
        title: "Jazz Fusion Groove",
        mode: "music",
        vocalGender: "none",
        style: `A sophisticated jazz fusion piece combining traditional jazz harmonies with modern electronic elements. Features a walking bass line, complex chord progressions, electric piano, saxophone solos, and a steady jazz drum pattern at 110 BPM.`,
        prompt: `A sophisticated jazz fusion piece combining traditional jazz harmonies with modern electronic elements. Features a walking bass line, complex chord progressions, electric piano, saxophone solos, and a steady jazz drum pattern at 110 BPM.`,
    },
    {
        id: "lyria-music-4",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-4.wav",
        title: "Desert Wind",
        mode: "music",
        vocalGender: "none",
        style: `An atmospheric world music piece inspired by desert landscapes, featuring traditional Middle Eastern instruments like oud, darbuka, and ney flute. The composition should have a mystical quality with drone notes, modal melodies, and rhythmic percussion patterns.`,
        prompt: `An atmospheric world music piece inspired by desert landscapes, featuring traditional Middle Eastern instruments like oud, darbuka, and ney flute. The composition should have a mystical quality with drone notes, modal melodies, and rhythmic percussion patterns.`,
    },
    {
        id: "lyria-music-5",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-5.wav",
        title: "Orchestral Epic",
        mode: "music",
        vocalGender: "none",
        style: `A grand orchestral composition with cinematic qualities, featuring full symphony orchestra including strings, brass, woodwinds, and percussion. The piece should build dramatically from quiet beginnings to powerful climaxes, with rich harmonies and dynamic contrasts.`,
        prompt: `A grand orchestral composition with cinematic qualities, featuring full symphony orchestra including strings, brass, woodwinds, and percussion. The piece should build dramatically from quiet beginnings to powerful climaxes, with rich harmonies and dynamic contrasts.`,
    },
    {
        id: "lyria-music-6",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-6.wav",
        title: "Lo-Fi Chill",
        mode: "music",
        vocalGender: "none",
        style: `A relaxed lo-fi hip-hop track with warm, vinyl-like textures, laid-back drum beats, jazzy piano samples, and atmospheric background sounds. The tempo should be around 80-85 BPM with a smooth, easy-going vibe perfect for studying or relaxation.`,
        prompt: `A relaxed lo-fi hip-hop track with warm, vinyl-like textures, laid-back drum beats, jazzy piano samples, and atmospheric background sounds. The tempo should be around 80-85 BPM with a smooth, easy-going vibe perfect for studying or relaxation.`,
    },
    {
        id: "lyria-music-7",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-7.wav",
        title: "Tropical House",
        mode: "music",
        vocalGender: "none",
        style: `An upbeat tropical house track with Caribbean-inspired rhythms, steel drum melodies, and infectious dance beats. Features a 120 BPM tempo with layered percussion, bright synthesizers, and summer vibes that make you want to dance.`,
        prompt: `An upbeat tropical house track with Caribbean-inspired rhythms, steel drum melodies, and infectious dance beats. Features a 120 BPM tempo with layered percussion, bright synthesizers, and summer vibes that make you want to dance.`,
    },
    {
        id: "lyria-music-8",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-8.wav",
        title: "Minimal Techno",
        mode: "music",
        vocalGender: "none",
        style: `A stripped-down minimal techno track with deep, hypnotic bass lines, precise drum programming, and subtle atmospheric elements. The composition should focus on rhythm and space, with a steady 125 BPM tempo and gradual textural evolution.`,
        prompt: `A stripped-down minimal techno track with deep, hypnotic bass lines, precise drum programming, and subtle atmospheric elements. The composition should focus on rhythm and space, with a steady 125 BPM tempo and gradual textural evolution.`,
    },
    {
        id: "lyria-music-9",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-9.wav",
        title: "Acoustic Folk",
        mode: "music",
        vocalGender: "none",
        style: `A warm acoustic folk piece featuring fingerpicked guitar, gentle vocals, and natural instrumentation. The song should have an intimate, storytelling quality with simple chord progressions, organic textures, and heartfelt melodies.`,
        prompt: `A warm acoustic folk piece featuring fingerpicked guitar, gentle vocals, and natural instrumentation. The song should have an intimate, storytelling quality with simple chord progressions, organic textures, and heartfelt melodies.`,
    },
    {
        id: "lyria-music-10",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-10.wav",
        title: "Space Ambient",
        mode: "music",
        vocalGender: "none",
        style: `An expansive space ambient composition with ethereal textures, distant cosmic sounds, and floating synthesizer pads. The piece should create a sense of infinite space and time, with slow-evolving soundscapes and otherworldly atmospheres.`,
        prompt: `An expansive space ambient composition with ethereal textures, distant cosmic sounds, and floating synthesizer pads. The piece should create a sense of infinite space and time, with slow-evolving soundscapes and otherworldly atmospheres.`,
    },
    {
        id: "lyria-music-11",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-11.wav",
        title: "Blues Rock",
        mode: "music",
        vocalGender: "none",
        style: `A powerful blues rock instrumental with electric guitar riffs, driving drums, and soulful solos. The track should have a classic blues structure with modern rock energy, featuring gritty guitar tones and dynamic rhythm section.`,
        prompt: `A powerful blues rock instrumental with electric guitar riffs, driving drums, and soulful solos. The track should have a classic blues structure with modern rock energy, featuring gritty guitar tones and dynamic rhythm section.`,
    },
    {
        id: "lyria-music-12",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-12.wav",
        title: "Chillwave",
        mode: "music",
        vocalGender: "none",
        style: `A dreamy chillwave track with nostalgic 80s synthesizer sounds, reverb-drenched melodies, and laid-back drum patterns. The composition should have a hazy, retro-futuristic quality with warm analog textures and smooth transitions.`,
        prompt: `A dreamy chillwave track with nostalgic 80s synthesizer sounds, reverb-drenched melodies, and laid-back drum patterns. The composition should have a hazy, retro-futuristic quality with warm analog textures and smooth transitions.`,
    },
    {
        id: "lyria-music-13",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-13.wav",
        title: "Classical Piano",
        mode: "music",
        vocalGender: "none",
        style: `A beautiful classical piano piece in the romantic style, featuring expressive melodies, rich harmonies, and dynamic contrasts. The composition should showcase the full range of the piano with flowing arpeggios, dramatic crescendos, and emotional depth.`,
        prompt: `A beautiful classical piano piece in the romantic style, featuring expressive melodies, rich harmonies, and dynamic contrasts. The composition should showcase the full range of the piano with flowing arpeggios, dramatic crescendos, and emotional depth.`,
    },
    {
        id: "lyria-music-14",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-14.wav",
        title: "Downtempo Trip-Hop",
        mode: "music",
        vocalGender: "none",
        style: `A moody downtempo trip-hop track with atmospheric beats, deep bass lines, and ethereal vocal samples. The piece should have a dark, cinematic quality with layered textures, subtle percussion, and hypnotic rhythms around 90 BPM.`,
        prompt: `A moody downtempo trip-hop track with atmospheric beats, deep bass lines, and ethereal vocal samples. The piece should have a dark, cinematic quality with layered textures, subtle percussion, and hypnotic rhythms around 90 BPM.`,
    },
    {
        id: "lyria-music-15",
        audioSrc1: "https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-15.wav",
        title: "World Fusion",
        mode: "music",
        vocalGender: "none",
        style: `A vibrant world fusion piece combining elements from multiple cultural traditions, featuring global percussion, ethnic instruments, and contemporary electronic production. The track should celebrate cultural diversity while maintaining musical coherence and danceable rhythms.`,
        prompt: `A vibrant world fusion piece combining elements from multiple cultural traditions, featuring global percussion, ethnic instruments, and contemporary electronic production. The track should celebrate cultural diversity while maintaining musical coherence and danceable rhythms.`,
    },
];

export default function GoogleLyriaMusicPage() {
    return (
        <div {...stylex.props(styles.modelPage)}>
            {/* Header Navigation */}
            <Link to="/" {...stylex.props(styles.backLink)}>
                <ArrowLeft size={20} />
                Back to Home
            </Link>

            {/* Hero Section */}
            <section {...stylex.props(styles.heroSection, styles.musicHeroAnimated)}>
                <div {...stylex.props(styles.heroContent)}>
                    <h2 {...stylex.props(styles.heroTitle)}>{MODEL_CONFIG.name.toUpperCase()}</h2>
                    <p {...stylex.props(styles.heroDescription)}>
                        Experience revolutionary <strong>AI Music Generation</strong> with Google DeepMind's Lyria.
                        Create <strong>high-fidelity compositions</strong> with granular creative control, featuring <strong>real-time generation</strong> and <strong>professional-grade audio</strong>.
                        <br />
                        Powered by <a href={MODEL_CONFIG.providerUrl} target="_blank" rel="noopener noreferrer">{MODEL_CONFIG.providerName}</a>
                    </p>
                    <div {...stylex.props(styles.heroStats)}>
                        <div {...stylex.props(styles.stat)}>
                            <Music size={20} {...stylex.props(styles.statSvg)} />
                            <span>High-Fidelity Audio</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Clock size={20} {...stylex.props(styles.statSvg)} />
                            <span>Real-Time Generation</span>
                        </div>
                        <div {...stylex.props(styles.stat)}>
                            <Settings size={20} {...stylex.props(styles.statSvg)} />
                            <span>Granular Control</span>
                        </div>
                    </div>
                    <div {...stylex.props(styles.creditCostBadge)}>{MODEL_CONFIG.creditsPerRequest} credits per request</div>

                    {/* Featured Music Player */}
                    <div {...stylex.props(styles.heroVoicePlayer)}>
                        <audio controls {...stylex.props(styles.heroAudioPlayer)}>
                            <source src="https://cdn.aibrush.co/docs/audio/models/google-lyria/sample-hero-music.wav" type="audio/wav" />
                            Your browser does not support the audio element.
                        </audio>
                    </div>
                </div>

                {/* Floating Icons Container */}
                <div {...stylex.props(styles.floatingIconsContainer)}>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon1)}>
                        <Music size={120} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon2)}>
                        <Clock size={80} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon3)}>
                        <Settings size={90} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                    <div {...stylex.props(styles.floatingIcon, styles.floatingIcon4)}>
                        <Zap size={50} {...stylex.props(styles.floatingIconSvg)} />
                    </div>
                </div>
            </section>

            {/* AI Assistance Note */}
            <div {...stylex.props(styles.importantNote)}>
                <div {...stylex.props(styles.noteIcon)}>
                    <div {...stylex.props(styles.inlineChatButton)}>
                        <BotMessageSquare size={22} />
                    </div>
                </div>
                <div {...stylex.props(styles.noteText)}>
                    <strong>Need help with music descriptions or prompt writing?</strong> Click the
                    chat icon in the top-right corner of any component to get AI assistance with your music creation!
                </div>
            </div>

            {/* Music Gallery */}
            <TextToMusicGallery
                modelKey={MODEL_CONFIG.modelKey}
                modelLabel={MODEL_CONFIG.shortName}
                modelNote={`This model costs <strong>${MODEL_CONFIG.creditsPerRequest} credits</strong> per request.`}
                title="AI Music Generation Gallery"
                description={`Explore the exceptional capabilities of <a href='${MODEL_CONFIG.providerUrl}' target='_blank' rel='noopener noreferrer'>${MODEL_CONFIG.providerName}</a> through these AI-generated music samples. With advanced neural networks and granular creative controls, Lyria delivers high-fidelity, professional-grade music compositions. <strong>Click on any music sample</strong> to hear how it was created and see the parameters used.`}
                items={textToMusicGalleryItems} />
        </div>
    )
}